import React, { useState, useEffect, useMemo } from 'react';
import ReactSwitch from 'react-switch';
import cn from 'classnames';
import _ from 'lodash';
import cogoToast from 'cogo-toast';
import { confirmAlert } from 'react-confirm-alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faMinusCircle, faPlusCircle, faChevronDown, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import Tooltip from '../../General/Tooltip';
import './UrlSiteRuleCreate.scss';

const UrlSiteRuleCreate = props => {
  const {
    selectedSiteRule,
    handleUrlSiteRuleUpdate,
    handleUrlSiteRuleCreate,
    handleUrlSiteRuleTest,
    setHasUnsavedChanges,
    checkForExistingSiteRule,
    isSaving
  } = props;
  // Rule fields
  const [domain, setDomain] = useState(selectedSiteRule?.domain || '');
  const [isActive, setIsActive] = useState(selectedSiteRule ? Boolean(selectedSiteRule.isActive) : true);
  const [paths, setPaths] = useState(selectedSiteRule?.paths ? selectedSiteRule.paths.join(',') : '');
  const [preserveParams, setPreserveParams] = useState(
    Array.isArray(selectedSiteRule?.preserveParams) && selectedSiteRule.preserveParams.length === 0
      ? 'BYPASS_DEFAULT'
      : selectedSiteRule?.preserveParams
      ? selectedSiteRule.preserveParams.join(',')
      : ''
  );
  const [pathReplacements, setPathReplacements] = useState(selectedSiteRule?.pathReplacements ?? []);
  const [pathPrefixes, setPathPrefixes] = useState(selectedSiteRule?.pathPrefixes ? selectedSiteRule.pathPrefixes.join(',') : '');

  const [pathRegexPattern, setPathRegexPattern] = useState(selectedSiteRule?.pathRegexPattern || '');
  const [pathRegexFlags, setPathRegexFlags] = useState(selectedSiteRule?.pathRegexFlags || '');
  const [requiredParams, setRequiredParams] = useState(selectedSiteRule?.requiredParams ? selectedSiteRule.requiredParams.join(',') : '');
  const [forceWWW, setForceWWW] = useState(selectedSiteRule ? Boolean(selectedSiteRule.forceWWW) : false);
  const [domainAliases, setDomainAliases] = useState(selectedSiteRule?.domainAliases ? selectedSiteRule.domainAliases.join(',') : '');
  const [forceTrailingSlash, setForceTrailingSlash] = useState(selectedSiteRule ? Boolean(selectedSiteRule.forceTrailingSlash) : false);
  // Rule state
  const [testInput, setTestInput] = useState('');
  const [testOutput, setTestOutput] = useState('');
  const [hasTested, setHasTested] = useState(false);
  const [isTestExpanded, setIsTestExpanded] = useState(false);
  // Other
  const isUpdate = Boolean(selectedSiteRule);
  const isBypassPreserveParamsChecked = preserveParams === 'BYPASS_DEFAULT' || preserveParams.length > 0;

  useEffect(() => {
    if (selectedSiteRule) {
      setDomain(selectedSiteRule.domain || '');
      setIsActive(Boolean(selectedSiteRule.isActive));
      setPaths(selectedSiteRule.paths?.join(',') || '');
      setPreserveParams(
        Array.isArray(selectedSiteRule?.preserveParams) && selectedSiteRule.preserveParams.length === 0
          ? 'BYPASS_DEFAULT'
          : selectedSiteRule?.preserveParams
          ? selectedSiteRule.preserveParams.join(',')
          : ''
      );
      setPathPrefixes(selectedSiteRule.pathPrefixes?.join(',') || '');
      setPathReplacements(selectedSiteRule.pathReplacements || []);
      setPathRegexPattern(selectedSiteRule.pathRegexPattern || '');
      setPathRegexFlags(selectedSiteRule.pathRegexFlags || '');
      setRequiredParams(selectedSiteRule.requiredParams?.join(',') || '');
      setForceWWW(Boolean(selectedSiteRule.forceWWW));
      setDomainAliases(selectedSiteRule.domainAliases?.join(',') || '');
      setForceTrailingSlash(Boolean(selectedSiteRule.forceTrailingSlash));

      setTestInput('');
      setTestOutput('');
      setHasTested(false);
      setIsTestExpanded(false);
    }
  }, [selectedSiteRule]);

  const handleTrimmedInputChange = (value, setter) => {
    setter(value.trim());
  };

  const handleCommaSeparatedFieldChange = (value, setter) => {
    const hasTrailingComma = value.endsWith(',');
    let trimValue = hasTrailingComma ? value.slice(0, -1) : value;
    let newValue = trimValue
      .split(',')
      .map(item => item.trim())
      .filter(item => item);

    newValue = newValue.length > 0 ? `${newValue}${hasTrailingComma ? ',' : ''}` : '';
    setter(newValue);
  };

  const handleDeleteReplacement = index => {
    setPathReplacements(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddReplacement = () => {
    setPathReplacements(prev => [...(prev || []), { from: '', flags: '', to: '' }]);
  };

  const handleReplacementChange = (index, field, value) => {
    setPathReplacements(prev =>
      prev.map((replacement, i) => {
        if (i === index) {
          return { ...replacement, [field]: value };
        }
        return replacement;
      })
    );
  };

  const arePathReplacementsEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    if (a.length === 0 && b.length === 0) return true;

    const makeKey = item => `${item.to}-${item.from}-${item.flags}`;

    const aKeys = a.map(makeKey).sort();
    const bKeys = b.map(makeKey).sort();

    return aKeys.every((key, idx) => key === bKeys[idx]);
  };

  const hasChanges = useMemo(() => {
    if (!isUpdate) return false;

    const hasActiveChanges = isActive !== !!selectedSiteRule.isActive;
    const hasPathsChanges = paths !== (selectedSiteRule.paths ? selectedSiteRule.paths.join(',') : '');
    const hasPathRegexPatternChanges = pathRegexPattern !== (selectedSiteRule.pathRegexPattern || '');
    const hasPathRegexFlagsChanges = pathRegexFlags !== (selectedSiteRule.pathRegexFlags || '');
    const hasPathPrefixesChanges = pathPrefixes !== (selectedSiteRule.pathPrefixes ? selectedSiteRule.pathPrefixes.join(',') : '');

    const comparePreserveParams =
      Array.isArray(selectedSiteRule.preserveParams) && selectedSiteRule.preserveParams.length === 0
        ? 'BYPASS_DEFAULT'
        : selectedSiteRule.preserveParams?.join(',') || '';
    const hasPreserveParamsChanges = preserveParams !== comparePreserveParams;
    const hasRequiredParamsChanges = requiredParams !== (selectedSiteRule.requiredParams ? selectedSiteRule.requiredParams.join(',') : '');

    const hasPathReplacementsChanges = !arePathReplacementsEqual(pathReplacements, selectedSiteRule.pathReplacements || []);
    const hasDomainAliasesChanges = domainAliases !== (selectedSiteRule.domainAliases ? selectedSiteRule.domainAliases.join(',') : '');
    const hasForceWWWChanges = forceWWW !== !!selectedSiteRule.forceWWW;
    const hasForceTrailingSlashChanges = forceTrailingSlash !== !!selectedSiteRule.forceTrailingSlash;

    return (
      hasActiveChanges ||
      hasPathsChanges ||
      hasPathPrefixesChanges ||
      hasPreserveParamsChanges ||
      hasPathReplacementsChanges ||
      hasRequiredParamsChanges ||
      hasPathRegexPatternChanges ||
      hasPathRegexFlagsChanges ||
      hasDomainAliasesChanges ||
      hasForceWWWChanges ||
      hasForceTrailingSlashChanges
    );
  }, [
    isUpdate,
    isActive,
    paths,
    preserveParams,
    pathReplacements,
    pathPrefixes,
    pathRegexPattern,
    pathRegexFlags,
    requiredParams,
    domainAliases,
    forceWWW,
    forceTrailingSlash,
    selectedSiteRule
  ]);

  useEffect(() => {
    setHasUnsavedChanges(hasChanges);

    if (hasChanges) {
      setHasTested(false);
    }
  }, [hasChanges, setHasUnsavedChanges]);

  const getValidationErrors = async () => {
    const errors = [];
    // 1. check domain is defined
    if (!domain?.length) {
      errors.push('Domain is required');
    }

    // 2. Check test has been run
    if (!hasTested) {
      errors.push('Please run a test on this rule before saving');
    }

    // 3. in create mode, check rule with domain doesn't already exist
    if (!isUpdate) {
      try {
        const exists = await checkForExistingSiteRule(domain);

        if (exists) {
          errors.push('A site rule with this domain already exists');
        }
      } catch (error) {
        console.error('Error checking for existing site rule:', error);
        errors.push('There was an issue validating the site rule domain, please try again');
      }
    }

    // 4. check pathReplacements are valid
    if (pathReplacements && pathReplacements.length) {
      pathReplacements.forEach((replacement, index) => {
        if (!replacement.from) {
          errors.push(`Path Replacement ${index + 1} is missing 'from' value`);
        }
        if (!replacement.to) {
          errors.push(`Path Replacement ${index + 1} is missing 'to' value`);
        }
      });
    }

    return errors;
  };

  const getBaseRulePayload = () => {
    // prepare payload for DB
    const basePayload = {
      isActive,
      domainAliases: domainAliases
        ? JSON.stringify(
            domainAliases
              .split(',')
              .map(item => item.trim())
              .filter(item => item)
          )
        : null,
      forceWWW,
      forceTrailingSlash,
      paths: paths
        ? JSON.stringify(
            paths
              .split(',')
              .map(item => item.trim())
              .filter(item => item)
          )
        : null,
      pathPrefixes: pathPrefixes
        ? JSON.stringify(
            pathPrefixes
              .split(',')
              .map(item => item.trim())
              .filter(item => item)
          )
        : null,
      preserveParams:
        preserveParams === 'BYPASS_DEFAULT'
          ? JSON.stringify([])
          : preserveParams
          ? JSON.stringify(
              preserveParams
                .split(',')
                .map(item => item.trim())
                .filter(item => item)
            )
          : null,
      requiredParams: requiredParams
        ? JSON.stringify(
            requiredParams
              .split(',')
              .map(item => item.trim())
              .filter(item => item)
          )
        : null,
      pathRegexPattern: pathRegexPattern || null,
      pathRegexFlags: pathRegexPattern ? pathRegexFlags : null,
      pathReplacements: pathReplacements?.length
        ? JSON.stringify(
            pathReplacements.map(item => ({
              from: item.from,
              to: item.to,
              flags: item.flags
            }))
          )
        : null
    };

    return basePayload;
  };

  const handleSaveSiteRule = async () => {
    const validationErrors = await getValidationErrors();
    if (validationErrors.length) {
      cogoToast.error('Please fix the following submission errors before saving: ' + validationErrors[0], { hideAfter: 8 });
      return;
    }

    const basePayload = getBaseRulePayload();

    if (isUpdate) {
      const updatePayload = {
        ...basePayload,
        needsUpdate: selectedSiteRule.needsDeletion ? false : true // if rule is marked for deletion, don't also mark for update
      };

      await handleUrlSiteRuleUpdate(selectedSiteRule.id, updatePayload);
    } else {
      const createPayload = {
        ...basePayload,
        domain: domain.trim(),
        needsUpdate: true
      };

      await handleUrlSiteRuleCreate(createPayload);
    }
  };

  const handleDeleteSiteRule = async () => {
    confirmAlert({
      title: 'Are you sure?',
      message: `Are you sure you want to delete this site rule? This will schedule the rule for deletion and mark the rule inactive until it is fully deleted.`,
      buttons: [
        { label: 'No', className: 'cancel', onClick: () => {} },
        {
          label: 'Yes',
          onClick: async () => {
            const updatePayload = {
              needsDeletion: true,
              needsUpdate: false,
              isActive: false
            };
            await handleUrlSiteRuleUpdate(selectedSiteRule.id, updatePayload);
          }
        }
      ]
    });
  };

  const runRuleTest = async () => {
    if (!testInput?.length) {
      return;
    }

    const constructedCurrentRule = {
      ...getBaseRulePayload(),
      domain: domain.trim()
    };

    const testPayload = {
      url: testInput,
      siteRule: constructedCurrentRule
    };

    const testOutput = await handleUrlSiteRuleTest(testPayload);

    if (!_.isNil(testOutput)) {
      // successful test
      setTestOutput(testOutput);
      setHasTested(true);
    }
  };

  const isDeleteDisabled = hasChanges || isSaving;
  const isSaveDisabled = (isUpdate && !hasChanges) || isSaving;

  return (
    <div className='create-rule-container'>
      <div className='create-rule-content'>
        <div className='edit-header'>
          <h2>{isUpdate ? 'Edit Site Rule' : 'Create Site Rule'}</h2>
          <div className='subheader'>
            Changes to site rules take effect on newly created link stems immediately. Existing links with old versions of site rules applied will be
            marked for nightly update upon creation or edit of a site rule.
          </div>
        </div>
        <div className='rule-details'>
          <div className='field'>
            <span className='label'>Domain</span>
            {isUpdate ? (
              <span className='value'>{domain}</span>
            ) : (
              <div className='value'>
                <input type='text' value={domain} onChange={e => handleTrimmedInputChange(e.target.value, setDomain)} />
              </div>
            )}
          </div>
          <div className={cn('field accordion', { expanded: isTestExpanded })}>
            <span className='label' onClick={() => setIsTestExpanded(prev => !prev)}>
              <FontAwesomeIcon icon={isTestExpanded ? faChevronDown : faChevronRight} />
              Test Rule
              <Tooltip message={'Run a test of this rule against an input URL'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='accordion-content'>
              <div className='sub-field-label'>Input URL</div>
              <div className='value'>
                <input type='text' value={testInput} onChange={e => setTestInput(e.target.value)} />
              </div>
              <div className='sub-field-label'>Output URL</div>
              <div className='value'>
                <input type='text' value={testOutput} disabled={true} />
              </div>
              <div className='field-submit-btn' onClick={runRuleTest}>
                Run Test
              </div>
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Status
              <Tooltip message={'Toggle this site rule on or off'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <div className='toggle'>
                <ReactSwitch
                  checked={isActive}
                  onChange={checked => setIsActive(checked)}
                  disabled={!!selectedSiteRule?.needsDeletion}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor='#11835a'
                  height={24}
                  width={43}
                  handleDiameter={21}
                />
              </div>
              {isActive ? 'Active' : 'Inactive'}
              <div className={cn('sync-status', { isDeletion: selectedSiteRule?.needsDeletion })}>
                {!!selectedSiteRule?.needsUpdate && ' (sync scheduled)'}
                {!!selectedSiteRule?.needsDeletion && ' (deletion scheduled)'}
              </div>
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Force WWW
              <Tooltip message={'Force www. for URL stems on this domain'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <div className='toggle'>
                <ReactSwitch
                  checked={forceWWW}
                  onChange={checked => setForceWWW(checked)}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor='#11835a'
                  height={24}
                  width={43}
                  handleDiameter={21}
                />
              </div>
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Force Trailing Slash
              <Tooltip message={'Force a trailing slash "/" for URL stems on this domain'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <div className='toggle'>
                <ReactSwitch
                  checked={forceTrailingSlash}
                  onChange={checked => setForceTrailingSlash(checked)}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor='#11835a'
                  height={24}
                  width={43}
                  handleDiameter={21}
                />
              </div>
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Domain Replacements
              <Tooltip message={'Comma-separated; Domains to fold into the main rule domain for URL stemming'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <input
                type='text'
                value={domainAliases}
                onChange={e => handleCommaSeparatedFieldChange(e.target.value, setDomainAliases)}
                placeholder='None'
              />
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Path Matches
              <Tooltip message={'Comma-separated; Exact path matches required for the rule to apply'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <input type='text' value={paths} onChange={e => handleCommaSeparatedFieldChange(e.target.value, setPaths)} placeholder='None' />
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Path Prefixes
              <Tooltip message={'Comma-separated; Path prefix matches required for the rule to apply'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <input
                type='text'
                value={pathPrefixes}
                onChange={e => handleCommaSeparatedFieldChange(e.target.value, setPathPrefixes)}
                placeholder='None'
              />
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Path Regex
              <Tooltip message={'Path regex required to match for the rule to apply'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='sub-field-label'>Regex</div>
            <div className='value'>
              <input type='text' value={pathRegexPattern} onChange={e => setPathRegexPattern(e.target.value)} placeholder='None' />
            </div>
            <div className='sub-field-label'>Flags</div>
            <div className='value'>
              <input type='text' value={pathRegexFlags} onChange={e => setPathRegexFlags(e.target.value)} placeholder='None' />
            </div>
          </div>
          <div className='field'>
            <span className='label clickable'>
              Path Replacements
              <Tooltip message={'Add a new replacement rule'}>
                <FontAwesomeIcon icon={faPlusCircle} onClick={() => handleAddReplacement()} />
              </Tooltip>
            </span>
            <div className='replacement-cards'>
              {(pathReplacements || []).map((replacement, index) => {
                const replacementKey = `replacement-${index}`;
                return (
                  <div className='replacement-card' key={replacementKey}>
                    <div className='card-header'>
                      Replacement {index + 1}
                      <Tooltip message={'Delete this replacement rule'}>
                        <FontAwesomeIcon icon={faMinusCircle} onClick={() => handleDeleteReplacement(index)} />
                      </Tooltip>
                    </div>
                    <div className='sub-field'>
                      <div className='sub-field-label'>From</div>
                      <div className='sub-field-value'>
                        <input type='text' value={replacement.from} onChange={e => handleReplacementChange(index, 'from', e.target.value)} />
                      </div>
                    </div>
                    <div className='sub-field'>
                      <div className='sub-field-label'>Flags</div>
                      <div className='sub-field-value'>
                        <input type='text' value={replacement.flags} onChange={e => handleReplacementChange(index, 'flags', e.target.value)} />
                      </div>
                    </div>
                    <div className='sub-field'>
                      <div className='sub-field-label'>To</div>
                      <div className='sub-field-value'>
                        <input type='text' value={replacement.to} onChange={e => handleReplacementChange(index, 'to', e.target.value)} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Required Params
              <Tooltip message={'Comma-separated; URL parameters required to be in the URL for the rule to apply'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <input
                type='text'
                value={requiredParams}
                onChange={e => handleCommaSeparatedFieldChange(e.target.value, setRequiredParams)}
                placeholder='None'
              />
            </div>
          </div>
          <div className='field'>
            <span className='label'>
              Preserve Params
              <Tooltip message={'Comma-separated; URL parameters to preserve in the stem'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </span>
            <div className='value'>
              <input
                type='text'
                value={preserveParams}
                onChange={e => handleCommaSeparatedFieldChange(e.target.value, setPreserveParams)}
                placeholder='None'
                disabled={preserveParams === 'BYPASS_DEFAULT'}
              />
            </div>
            <div className='value'>
              <div className='toggle'>
                <ReactSwitch
                  checked={isBypassPreserveParamsChecked}
                  onChange={checked => setPreserveParams(checked ? 'BYPASS_DEFAULT' : '')}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor='#11835a'
                  height={24}
                  width={43}
                  handleDiameter={21}
                  disabled={preserveParams.length > 0 && preserveParams !== 'BYPASS_DEFAULT'}
                />
              </div>
              {isBypassPreserveParamsChecked ? 'Bypass Default Preserve Params' : 'Use Default Preserve Params'}
              <Tooltip message={'Bypass preserving default params like pid, productId, id, etc.'}>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      <div className='footer-container'>
        {isUpdate && !selectedSiteRule.needsDeletion && (
          <div
            className={cn('action-button delete', { disabled: isDeleteDisabled })}
            onClick={() => {
              if (isDeleteDisabled) return;
              handleDeleteSiteRule();
            }}
          >
            {'DELETE RULE'}
          </div>
        )}
        <div
          className={cn('action-button', { disabled: isSaveDisabled })}
          onClick={() => {
            if (isSaveDisabled) return;
            handleSaveSiteRule();
          }}
        >
          {isUpdate ? 'SAVE' : 'CREATE RULE'}
        </div>
      </div>
    </div>
  );
};

export default UrlSiteRuleCreate;
